/**
 * Aperçu du Studio : la scène choisie, à l'instant choisi (curseur de temps),
 * dessinée par le MÊME moteur que le lecteur (`compileAnimation`,
 * `sceneStateAt`, `AnimationStage`). Repères sur le curseur : début des
 * phrases et des étapes ; « Lire la scène » la joue sans la voix. L'objet
 * choisi dans l'inspecteur est éclairé.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, SkipBack } from 'lucide-react';
import {
  compileAnimation,
  sceneStateAt,
  splitSentences,
  type CompiledAnimation,
  type JrafDocument,
} from '@jeraisonne/animation';
import { cn } from '@jeraisonne/ui';
import { AnimationStage, type StageHandle } from '@jeraisonne/ui/components/animations/stage';
import { AnimationMathText } from '@jeraisonne/ui/components/animations/animation-math';
import { animationAssetUrl } from '@/lib/animations/voice';

export function seconds1(ms: number): string {
  return `${(ms / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}\u202fs`;
}

export function StudioPreview({
  doc,
  sceneIndex,
  t,
  onTime,
  selectedObject,
  focusStep,
  onSelectStep,
}: {
  doc: JrafDocument;
  sceneIndex: number;
  /** instant dans la scène (au-delà de sa fin : son état final) */
  t: number;
  onTime: (t: number) => void;
  selectedObject: string | null;
  /** étape choisie dans l'inspecteur : le curseur va à sa fin */
  focusStep?: string | null;
  onSelectStep?: (stepId: string) => void;
}) {
  const anim: CompiledAnimation | null = useMemo(() => {
    try {
      return compileAnimation(doc);
    } catch {
      return null;
    }
  }, [doc]);
  const scene = anim?.scenes[Math.min(sceneIndex, (anim?.scenes.length ?? 1) - 1)] ?? null;
  const end = scene?.schedule.endMs ?? 0;
  const stageRef = useRef<StageHandle>(null);
  const [playing, setPlaying] = useState(false);

  // l'état de la scène à l'instant t ; l'objet choisi est éclairé
  useLayoutEffect(() => {
    if (!scene) return;
    const state = sceneStateAt(scene, Math.min(t, end));
    if (selectedObject && state[selectedObject])
      state[selectedObject] = { ...state[selectedObject], glow: 1 };
    stageRef.current?.apply(state);
  }, [scene, t, end, selectedObject]);

  // lecture sans voix
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    if (!playing) return;
    let last: number | null = null;
    let id = 0;
    const tick = (now: number) => {
      const dt = last === null ? 0 : Math.min(100, now - last);
      last = now;
      const next = tRef.current + dt;
      if (next >= end) {
        onTime(end);
        setPlaying(false);
        return;
      }
      onTime(next);
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [playing, end, onTime]);
  useEffect(() => setPlaying(false), [sceneIndex]);

  // étape choisie : on la voit finie
  const focusEnd = focusStep
    ? scene?.schedule.steps.find((s) => s.id === focusStep)?.endMs
    : undefined;
  useEffect(() => {
    if (focusEnd !== undefined) onTime(focusEnd);
  }, [focusStep, focusEnd, onTime]);

  if (!anim || !scene)
    return (
      <p className="text-ink-500 text-sm">Aperçu indisponible : le brouillon a des erreurs.</p>
    );

  const sentences = splitSentences(scene.scene.narration.text);
  const starts = scene.sentenceStartsMs;
  let current = 0;
  starts.forEach((s, i) => {
    if (s <= t) current = i;
  });
  const pct = (ms: number) => `${end > 0 ? Math.min(100, (ms / end) * 100) : 0}%`;

  return (
    <div className="space-y-2">
      <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-2xl shadow-md">
        <AnimationStage ref={stageRef} anim={anim} assetUrl={animationAssetUrl} />
      </div>
      <p
        className="text-ink-700 mx-auto min-h-[2.5rem] max-w-[300px] text-center text-[13px] leading-snug"
        aria-live="polite"
      >
        {sentences[current] ? <AnimationMathText>{sentences[current]!}</AnimationMathText> : null}
      </p>

      <div className="mx-auto max-w-[420px]">
        {/* repères : phrases (au-dessus), étapes (dessous) */}
        <div className="relative h-3" aria-hidden>
          {starts.map((s, i) => (
            <span
              key={i}
              className="bg-primary-500 absolute top-0 h-3 w-0.5 rounded-full"
              style={{ left: pct(s) }}
              title={`Phrase ${i + 1}`}
            />
          ))}
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(1, Math.round(end))}
          step={10}
          value={Math.round(Math.min(t, end))}
          onChange={(e) => onTime(Number(e.target.value))}
          aria-label="Temps dans la scène"
          aria-valuetext={`${seconds1(t)} sur ${seconds1(end)}`}
          className="accent-primary-500 w-full"
        />
        <div className="relative h-3">
          {scene.schedule.steps.map((st) => (
            <button
              key={st.id}
              type="button"
              onClick={() => {
                onTime(st.endMs);
                onSelectStep?.(st.id);
              }}
              className={cn(
                'absolute top-0 h-2 min-w-[4px] rounded-full',
                st.gate ? 'bg-accent-400' : 'bg-ink-300 hover:bg-ink-500',
              )}
              style={{
                left: pct(st.startMs),
                width: `max(4px, calc(${pct(st.endMs)} - ${pct(st.startMs)}))`,
              }}
              title={st.id}
              aria-label={`Aller à l’étape ${st.id}`}
            />
          ))}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (!playing && t >= end) onTime(0);
              setPlaying((p) => !p);
            }}
            className="bg-primary-500 hover:bg-primary-600 inline-flex h-10 w-10 items-center justify-center rounded-full text-white"
            aria-label={playing ? 'Pause' : 'Lire la scène'}
          >
            {playing ? (
              <Pause className="h-4 w-4" aria-hidden />
            ) : (
              <Play className="h-4 w-4" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setPlaying(false);
              onTime(0);
            }}
            className="text-ink-600 hover:bg-ink-100 inline-flex h-10 w-10 items-center justify-center rounded-full"
            aria-label="Début de la scène"
          >
            <SkipBack className="h-4 w-4" aria-hidden />
          </button>
          <span className="text-ink-600 text-[13px] tabular-nums">
            {seconds1(Math.min(t, end))} / {seconds1(end)} · phrase {current + 1}/
            {Math.max(1, sentences.length)}
          </span>
        </div>
      </div>
    </div>
  );
}
